import { useQuery } from "@tanstack/react-query";

import { ASSISTANT_ENABLED, ASSISTANT_STUB } from "@/config/features";
import { getAssistantMe } from "@/services/assistant.service";
import type { AssistantAllowance } from "@/types/assistant";

export interface AssistantAccess {
  /** Still asking the server. */
  isLoading: boolean;
  /** The panel and its button render. */
  enabled: boolean;
  /** Replaying fixtures instead of calling the backend. */
  stub: boolean;
  /** The month's usage before the first turn; null until known or when disabled. */
  allowance: AssistantAllowance | null;
  refetch: () => void;
}

/**
 * The one place that decides whether the assistant renders for the signed-in
 * brand. The server answers through GET /assistant/me, which folds in
 * ASSISTANT_ENABLED, the AI_ASSISTANT plan feature and the beta shop list,
 * so the client never checks the plan feature itself. VITE_ASSISTANT_ENABLED
 * is the client-side master switch; VITE_ASSISTANT_STUB implies it and skips
 * the server. React-query dedupes the call across the launcher and panel.
 */
export function useAssistantAccess(): AssistantAccess {
  const wanted = ASSISTANT_ENABLED || ASSISTANT_STUB;
  const query = useQuery({
    queryKey: ["assistant", "me"],
    queryFn: getAssistantMe,
    enabled: wanted,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  return {
    isLoading: wanted && query.isLoading,
    enabled: wanted && !!query.data?.enabled,
    stub: ASSISTANT_STUB,
    allowance: query.data?.allowance ?? null,
    refetch: () => void query.refetch(),
  };
}
